import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowUpRight, PlusCircle, Trash2, Upload } from 'lucide-react';
import PortfolioFileUpload from './PortfolioFileUpload';
import BrokerConnection from './BrokerConnection';
import { useToast } from '@/components/ui/use-toast';

interface ProfileCurrentPortfolioProps {
  data: any;
  updateData: (data: any) => void;
}

interface Investment {
  id: string;
  category: string;
  name: string;
  value: string;
  purchaseDate: string;
  purpose: string;
}

const ProfileCurrentPortfolio = ({ data, updateData }: ProfileCurrentPortfolioProps) => {
  const { toast } = useToast();
  const [investments, setInvestments] = useState<Investment[]>(
    data.investments || []
  );
  const [skipStep, setSkipStep] = useState(data.skipStep || false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [isBrokerConnected, setIsBrokerConnected] = useState(!!localStorage.getItem('kite_connected'));
  
  const handleSkip = () => {
    setSkipStep(true);
    updateData({ skipStep: true });
  };
  
  const addInvestment = () => {
    const newInvestment: Investment = {
      id: Date.now().toString(),
      category: '',
      name: '',
      value: '',
      purchaseDate: '',
      purpose: ''
    };
    
    const updatedInvestments = [...investments, newInvestment];
    setInvestments(updatedInvestments);
    updateData({ investments: updatedInvestments, skipStep: false });
  };
  
  const removeInvestment = (id: string) => {
    const updatedInvestments = investments.filter(inv => inv.id !== id);
    setInvestments(updatedInvestments);
    updateData({ investments: updatedInvestments, skipStep: false });
  };
  
  const updateInvestment = (id: string, field: keyof Investment, value: string) => {
    const updatedInvestments = investments.map(inv => 
      inv.id === id ? { ...inv, [field]: value } : inv
    );
    setInvestments(updatedInvestments);
    updateData({ investments: updatedInvestments, skipStep: false });
  };
  
  const handleFileUploadComplete = (portfolioData: Investment[]) => {
    const newInvestments = [...investments, ...portfolioData];
    setInvestments(newInvestments);
    updateData({ investments: newInvestments, skipStep: false });
    setShowFileUpload(false);
  };
  
  const handleBrokerConnectionComplete = (result) => {
    if (result.connected) {
      setIsBrokerConnected(true);
      
      // Add broker investments to the current list
      if (result.investments && result.investments.length > 0) {
        const updatedInvestments = [...investments, ...result.investments];
        setInvestments(updatedInvestments);
        updateData({ investments: updatedInvestments, skipStep: false });
        
        toast({
          title: "Portfolio Imported",
          description: `Successfully imported ${result.investments.length} investments from Zerodha`,
          variant: "success"
        });
      }
    } else {
      setIsBrokerConnected(false);
    }
  };
  
  if (skipStep) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold mb-4">Current Portfolio Details</h2>
        <p className="text-white/70 mb-6">
          You've chosen to skip this step. You can always come back and add your current investments later.
        </p>
        <Button 
          variant="outline" 
          className="border-[#9E7C47] text-[#9E7C47]"
          onClick={() => setSkipStep(false)}
        >
          Add investments now
        </Button>
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Current Portfolio Details</h2>
        <Button variant="link" onClick={handleSkip} className="text-white/70">
          Skip this step
        </Button>
      </div>
      
      <p className="text-white/70 mb-6">
        Add your current investments to help us better understand your portfolio and provide more personalized recommendations.
      </p>
      
      {!showFileUpload && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Button 
            variant="outline" 
            className="border-[#9E7C47]/50 hover:bg-[#9E7C47]/10 p-6 h-auto flex-col items-center justify-center text-left"
            onClick={() => setShowFileUpload(true)}
          >
            <div className="flex w-full items-center">
              <Upload className="h-5 w-5 mr-2 text-[#9E7C47]" />
              <span>Upload Portfolio</span>
            </div>
            <p className="text-xs text-white/60 mt-2 w-full">
              Import your investments from a spreadsheet or CSV file
            </p>
          </Button>
        </div>
      )}
      
      {showFileUpload ? (
        <div className="mb-6">
          <PortfolioFileUpload onUploadComplete={handleFileUploadComplete} />
          <Button 
            variant="ghost" 
            onClick={() => setShowFileUpload(false)} 
            className="mt-4 text-white/70"
          >
            Cancel
          </Button>
        </div>
      ) : null}
      
      <div className="mb-8">
        <BrokerConnection onConnectionComplete={handleBrokerConnectionComplete} />
      </div>
      
      {investments.length > 0 ? (
        <div className="space-y-6">
          {investments.map((investment) => (
            <div key={investment.id} className="p-4 rounded-md border border-[#9E7C47]/20 bg-[#9E7C47]/5">
              <div className="flex justify-between items-start mb-4">
                <h3 className="font-medium">Investment Details</h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => removeInvestment(investment.id)}
                  className="h-8 px-2 text-destructive hover:text-destructive/90"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`category-${investment.id}`}>Category</Label>
                  <Select
                    value={investment.category}
                    onValueChange={(value) => updateInvestment(investment.id, 'category', value)}
                  >
                    <SelectTrigger id={`category-${investment.id}`} className="bg-[#9E7C47]/10 border-[#9E7C47]/20 mt-1 text-white">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-black border-[#9E7C47]/30">
                      <SelectItem value="stocks">Stocks</SelectItem>
                      <SelectItem value="mutual-funds">Mutual Funds</SelectItem>
                      <SelectItem value="fixed-deposits">Fixed Deposits</SelectItem>
                      <SelectItem value="real-estate">Real Estate</SelectItem>
                      <SelectItem value="gold">Gold</SelectItem>
                      <SelectItem value="bonds">Bonds</SelectItem>
                      <SelectItem value="etfs">ETFs</SelectItem>
                      <SelectItem value="others">Others</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor={`name-${investment.id}`}>Name</Label>
                  <Input
                    id={`name-${investment.id}`}
                    placeholder="Enter investment name"
                    value={investment.name}
                    onChange={(e) => updateInvestment(investment.id, 'name', e.target.value)}
                    className="bg-black border-[#9E7C47]/20 mt-1 text-white"
                  />
                </div>
                
                <div>
                  <Label htmlFor={`value-${investment.id}`}>Current Value (₹)</Label>
                  <Input
                    id={`value-${investment.id}`}
                    placeholder="Enter amount"
                    type="number"
                    value={investment.value}
                    onChange={(e) => updateInvestment(investment.id, 'value', e.target.value)}
                    className="bg-black border-[#9E7C47]/20 mt-1 text-white"
                  />
                </div>
                
                <div>
                  <Label htmlFor={`date-${investment.id}`}>Purchase Date</Label>
                  <Input
                    id={`date-${investment.id}`}
                    type="date"
                    value={investment.purchaseDate}
                    onChange={(e) => updateInvestment(investment.id, 'purchaseDate', e.target.value)}
                    className="bg-black border-[#9E7C47]/20 mt-1 text-white"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label htmlFor={`purpose-${investment.id}`}>Purpose</Label>
                  <Input
                    id={`purpose-${investment.id}`}
                    placeholder="What are you investing for? (e.g., Retirement, Education)"
                    value={investment.purpose}
                    onChange={(e) => updateInvestment(investment.id, 'purpose', e.target.value)}
                    className="bg-black border-[#9E7C47]/20 mt-1 text-white"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 border border-dashed border-white/20 rounded-md">
          <p className="text-white/50 mb-4">No investments added yet</p>
          <Button 
            variant="default"
            onClick={addInvestment}
            className="bg-[#9E7C47] hover:bg-[#9E7C47]/90"
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Add Investment
          </Button>
        </div>
      )}
      
      {investments.length > 0 && (
        <Button
          variant="outline"
          onClick={addInvestment}
          className="mt-6 border-[#9E7C47] text-[#9E7C47]"
        >
          <PlusCircle className="h-4 w-4 mr-2" />
          Add Another Investment
        </Button>
      )}
    </div>
  );
};

export default ProfileCurrentPortfolio;
